// NewsActivationConfirmation — Phase 7a D9 operator opt-in.
//
// Shown on the System page after the 14-day shadow window has elapsed
// and the operator has reviewed the A/B divergence report. The operator
// clicks the CTA, confirms they understand the cost and scope, and the
// acknowledgment is persisted via the wizard event API. The operator
// then sets TRADINGPLATFORM_NEWS_INPUTS_ENABLED=1 (env var) and
// restarts the app process to enable live news-augmented scoring.
//
// This is intentionally separate from the first-run wizard: that
// wizard configures the news provider at setup time; this screen
// activates live use of news inputs after the shadow window proves
// the augmented context is safe.

import { useMemo, useState } from "react";
import { ApiClient } from "../api/client";
import { useFixtures } from "../api/dataSource";

type Props = {
  onDone: () => void;
  onCancel: () => void;
};

export const NewsActivationConfirmation = ({ onDone, onCancel }: Props) => {
  const fixturesActive = useFixtures();
  const apiClient = useMemo(() => new ApiClient(), []);

  const [ackShadowReviewed, setAckShadowReviewed] = useState(false);
  const [ackCostUnderstood, setAckCostUnderstood] = useState(false);
  const [ackNoGuarantee, setAckNoGuarantee] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canConfirm =
    ackShadowReviewed && ackCostUnderstood && ackNoGuarantee && !submitting;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (fixturesActive) {
        // eslint-disable-next-line no-console
        console.info("[news-activation] news_inputs_activated (fixtures)");
      } else {
        await apiClient.postWizardEvent({
          event_type: "news_inputs_activated",
          payload: {
            shadow_reviewed: true,
            cost_acknowledged: true,
            activated_at: new Date().toISOString(),
          },
        });
      }
      onDone();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Could not record acknowledgment. Check the network and retry.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tp-wizard-back" role="dialog" aria-modal="true">
      <div className="tp-wizard" style={{ maxWidth: 560 }}>
        <header className="tp-wizard-head">
          <div className="title">Enable news and expanded AI inputs</div>
        </header>

        <div className="tp-wizard-body">
          <p className="tp-wizard-lede">
            You are about to enable live news-augmented AI scoring. This
            replaces the current indicator-only AI calls with calls that
            include sanitized news headlines, fundamentals, sector context,
            and macro context (Phase 7a D9).
          </p>

          <section className="tp-wizard-section">
            <header>What changes</header>
            <ul
              style={{
                fontSize: 13,
                paddingLeft: 18,
                color: "var(--tp-fg-1)",
                lineHeight: 1.6,
              }}
            >
              <li>
                Live AI calls will include up to 5 recent news headlines per
                symbol (sanitized per PRD §5.8).
              </li>
              <li>
                Fundamentals, sector context, and macro context are included
                in each AI prompt.
              </li>
              <li>
                <strong>
                  TRADINGPLATFORM_NEWS_INPUTS_ENABLED=1
                </strong>{" "}
                must be set in the environment and the app process must be
                restarted before the change takes effect.
              </li>
              <li>
                The shadow runner will stop firing once live scoring is
                news-augmented.
              </li>
            </ul>
          </section>

          <section className="tp-wizard-section">
            <header>Estimated additional cost</header>
            <p style={{ fontSize: 13, color: "var(--tp-fg-1)", marginTop: 4 }}>
              Each signal generation call fetches news via the configured
              provider API. At 505 symbols × 1 daily cycle, incremental data
              costs are approximately $5–15 / month (provider-dependent).
              AI scoring cost is unchanged (same token count per call).
            </p>
          </section>

          <div className="tp-ack" style={{ marginTop: 14 }}>
            <input
              id="ack-shadow-reviewed"
              type="checkbox"
              checked={ackShadowReviewed}
              onChange={(e) => setAckShadowReviewed(e.target.checked)}
            />
            <label htmlFor="ack-shadow-reviewed">
              I have reviewed the 14-day A/B shadow comparison report
              (GET /api/v1/news-shadow/summary) and the divergence rate is
              acceptable.
            </label>
          </div>

          <div className="tp-ack">
            <input
              id="ack-cost"
              type="checkbox"
              checked={ackCostUnderstood}
              onChange={(e) => setAckCostUnderstood(e.target.checked)}
            />
            <label htmlFor="ack-cost">
              I understand that news inputs add incremental provider API costs
              and that the monthly ceiling in system_config caps total AI
              spend.
            </label>
          </div>

          <div className="tp-ack">
            <input
              id="ack-no-guarantee"
              type="checkbox"
              checked={ackNoGuarantee}
              onChange={(e) => setAckNoGuarantee(e.target.checked)}
            />
            <label htmlFor="ack-no-guarantee">
              I understand: there is no published evidence that news-augmented
              AI scoring improves trading outcomes. The user-still-approves
              property is preserved.
            </label>
          </div>

          <div
            className="tp-banner info"
            style={{ marginTop: 14 }}
            role="status"
          >
            <span className="icon">ⓘ</span>
            <span>
              After confirming here, set{" "}
              <code>TRADINGPLATFORM_NEWS_INPUTS_ENABLED=1</code> in your
              environment and restart the app process. This screen records
              the acknowledgment in the audit log.
            </span>
          </div>
        </div>

        <footer className="tp-wizard-foot">
          <div className="left">
            <button
              type="button"
              className="tp-btn tp-btn-secondary"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
          <div className="right">
            {error && (
              <span
                role="alert"
                style={{
                  color: "var(--tp-bad, #c33)",
                  fontSize: 12,
                  marginRight: 12,
                }}
              >
                {error}
              </span>
            )}
            <button
              type="button"
              className="tp-btn tp-btn-primary"
              onClick={() => void handleConfirm()}
              disabled={!canConfirm}
            >
              {submitting ? "Saving…" : "Confirm and record acknowledgment"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};
